import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from '@reduxjs/toolkit';

import { sortAlphabetically } from '../../../helpers/comparators';
import {
    IGetGroupEmptyAssignmentsParams,
    restGetGroupAssignments,
    restGetGroupEmptyAssignments,
    restPatchGroupAssignments,
} from '../../../services/groups';
import { TRootState } from '../../../store';
import {
    restGetEmptyLocationGroupAssignments,
    restGetLocationGroupAssignments,
    restPatchLocationGroupAssignments,
} from '../../../services/locations';
import { formatAddress } from '../../../helpers/formatAddress';
import { processChunk } from '../../../components/TransferList/helpers/processChunk';

export interface IAssignmentsAccess {
    registers: boolean;
    locations: boolean;
}

export interface IGroupAssignment {
    registerId: number;
    registerType: string;
    type: string;
    name: string;
    relationId: string;
    address?: string;
}

export interface IPostGroupAssignment extends Omit<IGroupAssignment, 'name'> {}

interface IState {
    selectedGroupId: null | string;
    mode: null | 'preview' | 'edit' | 'create';
    emptyAssignments: IGroupAssignment[];
    emptyAssigmentsLoading: boolean;
    assignments: IGroupAssignment[];
}

const MAX_ITEM_LIMIT = 1000;

const initialState: IState = {
    selectedGroupId: null,
    mode: null,
    emptyAssignments: [],
    emptyAssigmentsLoading: false,
    assignments: [],
};

interface IGroupAssignmentCollection {
    withAddress: IGroupAssignment[];
    withoutAddress: IGroupAssignment[];
}

export const fetchGroupAssignments = createAsyncThunk(
    'get:ui/groups/assignments',
    async (params: { id: string; access: IAssignmentsAccess }) => {
        const { id, access } = params;
        const responses = await Promise.all([
            access.registers
                ? restGetGroupAssignments(id)
                : Promise.resolve([]),
            access.locations
                ? restGetLocationGroupAssignments(id)
                : Promise.resolve([]),
        ]);
        const locationsAsGroupAssignments = responses?.[1]?.map((location) => {
            return {
                relationId: location.relationId,
                registerId: -1,
                registerType: 'LOCATION',
                type: 'LOCATION',
                name: location.name,
                address: formatAddress(location),
            };
        });

        const groupAssignments = [
            ...responses?.[0],
            ...locationsAsGroupAssignments,
        ];
        return groupAssignments;
    }
);

export const fetchGroupEmptyAssignments = createAsyncThunk(
    'get:ui/groups/emptyAssignments',
    async (params: {
        queryParams: IGetGroupEmptyAssignmentsParams;
        access: IAssignmentsAccess;
    }) => {
        const { queryParams, access } = params;
        const responses = await Promise.all([
            access.registers
                ? restGetGroupEmptyAssignments({
                      ...queryParams,
                      tags:
                          queryParams.tags === 'LOCATION'
                              ? undefined
                              : queryParams.tags,
                  })
                : Promise.resolve([]),
            access.locations
                ? restGetEmptyLocationGroupAssignments({
                      ...queryParams,
                      name: undefined,
                      search: queryParams.name,
                  })
                : Promise.resolve([]),
        ]);
        const locationsAsGroupAssignments = responses?.[1]?.map((location) => {
            return {
                relationId: location.relationId,
                registerId: -1,
                registerType: 'LOCATION',
                type: 'LOCATION',
                name: location.name,
                address: formatAddress(location),
            };
        });

        const groupAssignments = [
            ...responses?.[0],
            ...locationsAsGroupAssignments,
        ];
        return groupAssignments;
    }
);

const patchLocationsGroupAssignments = async (
    id: string,
    added: IGroupAssignment[],
    removed: IGroupAssignment[]
) => {
    const locationAssignmentsDataChunk = Math.floor(
        (added.length + removed.length) / MAX_ITEM_LIMIT
    );

    if (locationAssignmentsDataChunk >= 1) {
        await processChunk(
            id,
            added,
            removed,
            restPatchLocationGroupAssignments
        );
        return Promise.resolve();
    }

    const response = await restPatchLocationGroupAssignments(id, {
        added,
        removed,
    });
    return response;
};

export const patchRegistersGroupAssignments = async (
    id: string,
    added: IPostGroupAssignment[],
    removed: IPostGroupAssignment[]
) => {
    const assignmentsDataChunk = Math.floor(
        (added.length + removed.length) / MAX_ITEM_LIMIT
    );

    if (assignmentsDataChunk >= 1) {
        await processChunk(id, added, removed, restPatchGroupAssignments);
        return Promise.resolve();
    }

    const response = await restPatchGroupAssignments(id, { added, removed });
    return response;
};

export const patchGroupAssignments = createAsyncThunk(
    'patch:ui/groups/assignments',
    async (payload: {
        id: string;
        data: {
            added: IGroupAssignmentCollection;
            removed: IGroupAssignmentCollection;
        };
        access: IAssignmentsAccess;
    }) => {
        const { id, data, access } = payload;

        const responses = await Promise.all([
            access.registers
                ? patchRegistersGroupAssignments(
                      id,
                      data.added.withoutAddress,
                      data.removed.withoutAddress
                  )
                : Promise.resolve(),
            access.locations
                ? patchLocationsGroupAssignments(
                      id,
                      data.added.withAddress,
                      data.removed.withAddress
                  )
                : Promise.resolve(),
        ]);
        return responses;
    }
);

const groupsSlice = createSlice({
    name: 'groupsSlice',
    initialState,
    reducers: {
        goToPreviewMode(state) {
            state.mode = 'preview';
        },
        enterCreateGroupMode(state) {
            state.mode = 'create';
            state.selectedGroupId = null;
        },
        enterEditGroupMode(state) {
            state.mode = 'edit';
        },
        resetMode(state) {
            state.mode = null;
        },
        clearEmptyAssignments(state) {
            state.emptyAssigmentsLoading = false;
            state.emptyAssignments = [];
        },
        selectGroup(state, action) {
            state.selectedGroupId = action.payload;
            state.mode = 'preview';
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchGroupAssignments.fulfilled, (state, action) => {
            const sortedAssigments = action.payload.sort((a, b) =>
                sortAlphabetically(a.name, b.name)
            );
            state.assignments = sortedAssigments;
        });
        builder.addCase(fetchGroupEmptyAssignments.pending, (state) => {
            state.emptyAssigmentsLoading = true;
        });
        builder.addCase(
            fetchGroupEmptyAssignments.fulfilled,
            (state, action) => {
                if (
                    action.meta.arg.queryParams.excludedGroupId ===
                    state.selectedGroupId
                ) {
                    state.emptyAssignments = action.payload;
                    state.emptyAssigmentsLoading = false;
                }
            }
        );
        builder.addCase(
            fetchGroupEmptyAssignments.rejected,
            (state, action) => {
                state.emptyAssignments = [];
                state.emptyAssigmentsLoading = false;
            }
        );
    },
});

export const getGroupsMode = (state: TRootState) => state.ui.groupsSlice.mode;

export const getSelectedGroupId = (state: TRootState) =>
    state.ui.groupsSlice.selectedGroupId;

export const getAssignments = (state: TRootState) =>
    state.ui.groupsSlice.assignments;

export const getEmptyAssignments = (state: TRootState) =>
    state.ui.groupsSlice.emptyAssignments;

export const getEmptyAssignmentsLoading = (state: TRootState) =>
    state.ui.groupsSlice.emptyAssigmentsLoading;

export const isGroupsPaneVisible = createSelector(
    [getGroupsMode],
    (mode) => !!mode
);

export const {
    enterCreateGroupMode,
    enterEditGroupMode,
    goToPreviewMode,
    resetMode,
    selectGroup,
    clearEmptyAssignments,
} = groupsSlice.actions;

export default groupsSlice.reducer;
